import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Archive, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { ConfirmDialog } from "@/components/ConfirmDialog";

interface ArchivedCompany {
  id: string;
  name: string;
  category: string;
  industry: string | null;
  archivedAt: string;
}

const categoryStyle: Record<string, string> = {
  "Buyer Network": "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  "Client": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  "Prospects": "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  "COI": "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  "Capital Providers": "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
};

export default function Archived() {
  const queryClient = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);

  const { data: archived = [], isLoading } = useQuery<ArchivedCompany[]>({
    queryKey: ["archived"],
    queryFn: async () => {
      const res = await fetch("/api/companies/archived", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/companies/${id}/restore`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error("Failed to restore");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["archived"] });
      toast.success("Company restored");
    },
    onError: () => toast.error("Failed to restore"),
  });

  const permanentDeleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/companies/${id}/permanent-delete`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["archived"] });
      toast.success("Permanently deleted");
    },
    onError: () => toast.error("Failed to delete"),
  });

  return (
    <AppLayout title="Archived">
      <div className="max-w-3xl mx-auto space-y-4">
        <div>
          <h1 className="text-2xl font-semibold">Archived</h1>
          <p className="text-sm text-muted-foreground">
            Deleted companies are kept for 30 days before permanent removal.
            {archived.length > 0 && ` ${archived.length} archived.`}
          </p>
        </div>

        {isLoading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Loading...</div>
        ) : archived.length === 0 ? (
          <div className="text-center py-16">
            <Archive className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">Nothing in the archive</p>
          </div>
        ) : (
          <div className="space-y-2">
            {archived.map((co) => (
              <div key={co.id} className="flex items-center justify-between border rounded-lg p-4 hover:bg-muted/30">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{co.name}</span>
                    <Badge className={`text-[10px] ${categoryStyle[co.category] || "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400"}`}>
                      {co.category}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {co.industry && <span>{co.industry} · </span>}
                    Archived {formatDistanceToNow(new Date(co.archivedAt), { addSuffix: true })}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => restoreMutation.mutate(co.id)}>
                    <RotateCcw className="w-3 h-3" /> Restore
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-red-600 gap-1 dark:text-red-400"
                    onClick={() => setPendingDelete({ id: co.id, name: co.name })}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(next) => !next && !permanentDeleteMutation.isPending && setPendingDelete(null)}
        title={pendingDelete ? `Permanently delete ${pendingDelete.name}?` : ""}
        description="This hard-deletes the record from the database. Contacts, deals, and activity history linked to this company are NOT preserved — the row and its references are gone for good. Use Restore instead if you might want this company back."
        confirmLabel="Permanently delete"
        cancelLabel="Cancel"
        variant="destructive"
        isLoading={permanentDeleteMutation.isPending}
        onConfirm={async () => {
          if (!pendingDelete) return;
          await permanentDeleteMutation.mutateAsync(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </AppLayout>
  );
}
